import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Download, ArrowRight, MessageCircle, BadgeCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const WHATSAPP_URL =
  'https://wa.me/393806331704?text=Ciao%20Victor%2C%20ho%20visto%20il%20tuo%20sito%20e%20vorrei%20parlarti%20di%20un%20progetto.';

const SKILLS = ['Power Apps', 'Power Automate', 'Power BI', 'SQL Server', 'Azure'];

const COMPANIES = ['Baker Hughes', 'Pharma Line', 'Focaccia Group', 'Angelica Wine'];

export const Hero = () => {
  const { t } = useTranslation();

  const stats = (t('hero.stats', { returnObjects: true }) as { value: string; label: string }[]) || [];

  const handleCVDownload = async () => {
    const candidates = [
      '/cv/victor_lazari_cv.pdf',
      '/cv/Victor-Lazari-CV.pdf',
      '/Victor-Lazari-CV.pdf'
    ];
    for (const url of candidates) {
      try {
        const res = await fetch(url, { method: 'HEAD' });
        if (res.ok) {
          const a = document.createElement('a');
          a.href = url;
          a.download = 'Victor-Lazari-CV.pdf';
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }
      } catch {}
    }
    // Fallback to HTML CV with print dialog if PDF not found
    window.open('/cv/victor_lazari_cv.html?print=1', '_blank');
  };

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative overflow-hidden pt-28 pb-16 md:pt-32 md:pb-20"
    >
      {/* Background: subtle grid + radial glow */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        />
        <div className="absolute -top-32 right-0 w-[36rem] h-[36rem] rounded-full bg-sky-200/40 dark:bg-cyan-500/10 blur-3xl" />
        <div className="absolute top-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 w-full grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-center">
        <div className="order-2 lg:order-1 text-left">
          {/* Availability badge */}
          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 mb-6"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-xs font-medium text-emerald-800 dark:text-emerald-300">{t('hero.badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.05 }}
            className="text-4xl md:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.1] text-slate-900 dark:text-gray-100"
          >
            {t('hero.title1')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-indigo-600 dark:from-cyan-400 dark:to-sky-400">
              {t('hero.titleAccent')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="mt-5 text-lg md:text-xl text-slate-600 dark:text-gray-300 max-w-2xl leading-relaxed"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* Skill chips */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="mt-6 flex flex-wrap gap-2"
          >
            {SKILLS.map((skill) => (
              <span
                key={skill}
                className="px-3 py-1 text-xs font-medium rounded-full bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700"
              >
                {skill}
              </span>
            ))}
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35 }}
            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
          >
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 dark:bg-cyan-600 text-white font-semibold shadow-lg shadow-slate-900/10 dark:shadow-cyan-900/30 hover:bg-slate-800 dark:hover:bg-cyan-500 hover:-translate-y-0.5 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              <span>{t('hero.ctaPrimary')}</span>
            </a>
            <Link
              to="/solutions"
              className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white dark:bg-gray-800 border border-slate-300 dark:border-gray-700 text-slate-900 dark:text-gray-100 font-semibold shadow-sm hover:border-slate-400 dark:hover:border-gray-500 hover:-translate-y-0.5 transition-all"
            >
              <span>{t('hero.ctaSecondary')}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <button
              onClick={handleCVDownload}
              className="inline-flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-gray-100 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>{t('downloadCV')}</span>
            </button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.45 }}
            className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-slate-200 dark:border-gray-700 bg-slate-200 dark:bg-gray-700"
          >
            {stats.map((s) => (
              <div key={s.label} className="bg-white dark:bg-gray-800/80 px-4 py-4">
                <div className="text-2xl font-bold text-slate-900 dark:text-gray-100 tabular-nums">{s.value}</div>
                <div className="mt-0.5 text-xs text-slate-500 dark:text-gray-400 leading-snug">{s.label}</div>
              </div>
            ))}
          </motion.div>

          {/* Trusted by */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2"
          >
            <span className="text-xs uppercase tracking-wider text-slate-400 dark:text-gray-500 font-medium">
              {t('hero.trustedLabel')}
            </span>
            {COMPANIES.map((c) => (
              <span key={c} className="text-sm font-semibold text-slate-400 dark:text-gray-500">
                {c}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Photo */}
        <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-full bg-gradient-to-tr from-sky-400/30 to-indigo-400/30 dark:from-cyan-500/20 dark:to-indigo-500/20 blur-2xl" aria-hidden="true" />
            <img
              src="https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/Victor%20Lazari%20IT%20Consultant.png"
              alt="Victor Lazari – Consulente Power Platform e Automazione"
              className="relative w-52 h-52 md:w-72 md:h-72 rounded-full object-cover ring-4 ring-white dark:ring-gray-800 shadow-2xl"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              width={288}
              height={288}
            />
            {/* Floating credential card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="absolute -bottom-3 left-1/2 -translate-x-1/2 lg:left-auto lg:-left-10 lg:translate-x-0 flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white/90 dark:bg-gray-800/90 backdrop-blur border border-slate-200 dark:border-gray-700 shadow-lg whitespace-nowrap"
            >
              <BadgeCheck className="w-5 h-5 text-sky-600 dark:text-cyan-400 flex-shrink-0" />
              <div className="leading-tight">
                <div className="text-sm font-semibold text-slate-900 dark:text-gray-100">{t('hero.photoBadgeTitle')}</div>
                <div className="text-xs text-slate-500 dark:text-gray-400">{t('hero.photoBadgeSub')}</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};
